import React, { FunctionComponent, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { ReactComponent as IconPlusSquare } from "../../../../icons/plus-square.svg";
import { countPages } from "../../../../utils/customization/configuration";
import { getNoCoverDefinitions } from "../../../../utils/customization/cover";
import Button from "../../../atoms/Button";
import ConfirmChangesModal from "../../modals/ConfirmChangesModal";
import "./style.scss";

interface Props {
  pageDefinitionKey: string;
  ppmm: number;
}

const AddPage: FunctionComponent<Props> = ({ pageDefinitionKey }) => {
  const intl = useIntl();
  const { dispatch, configuration, content, template } = useCustomizationContext();
  const [displayModal, setDisplayModal] = useState<boolean>(false);
  const [modalChecked, setModalChecked] = useState<boolean>(false);

  let pagesIncrement = 1;
  if (configuration && configuration.pages && configuration.pages.increment) {
    pagesIncrement = configuration.pages.increment;
  }

  const additionalPrice =
    configuration && configuration.additionalPrice ? configuration.additionalPrice : null;
  let formattedPrice: string | undefined;
  if (additionalPrice) {
    formattedPrice = intl.formatNumber(additionalPrice.cents / 100, {
      currency: additionalPrice.currency,
      style: "currency",
    });
  }

  const addPage = () => {
    dispatch({
      configuration,
      pageDefinitionKey,
      type: "addPage",
      withFocus: true,
    });
    setDisplayModal(false);
  };

  let onButtonClick = () => {
    addPage();
  };

  const minimumPages = (configuration && configuration.pages && configuration.pages.min) || 1;
  const noCoverPageDefinitions = getNoCoverDefinitions(template.pageDefinitions);
  const pageCount = useMemo(() => countPages(content, noCoverPageDefinitions), [
    content,
    noCoverPageDefinitions,
  ]);

  if (pagesIncrement > 1 && modalChecked === false && pageCount === minimumPages) {
    onButtonClick = () => {
      setDisplayModal(true);
    };
  }

  let addPageLabel;
  let modalTitleLabel;
  let modalLineLabel;
  if (configuration && configuration.pageName && configuration.pageName.singular) {
    if (pagesIncrement > 1 && configuration.pageName.plural) {
      addPageLabel = (
        <FormattedMessage
          id="kustom_AddPage_add_pack__generic"
          values={{ pageName: configuration.pageName.plural }}
        />
      );
      modalTitleLabel = (
        <FormattedMessage
          id="kustom_AddPage_add_pack_modal_title__generic"
          values={{
            count: pagesIncrement,
            pageName: configuration.pageName.plural,
          }}
        />
      );
      modalLineLabel = (
        <FormattedMessage
          id="kustom_AddPage_add_pack_modal_line1__generic"
          values={{
            pageName: configuration.pageName.singular,
            price: formattedPrice,
          }}
        />
      );
    } else {
      addPageLabel = (
        <FormattedMessage
          id="kustom_AddPage_add__generic"
          values={{ pageName: configuration.pageName.singular }}
        />
      );
    }
  } else {
    switch (content.templateType) {
      case "print":
        if (pagesIncrement > 1) {
          addPageLabel = <FormattedMessage id="kustom_AddPage_add_pack__print" />;
          modalTitleLabel = (
            <FormattedMessage
              id="kustom_AddPage_add_pack_modal_title__print"
              values={{ count: pagesIncrement }}
            />
          );
          modalLineLabel = (
            <FormattedMessage
              id="kustom_AddPage_add_pack_modal_line1__print"
              values={{ price: formattedPrice }}
            />
          );
        } else {
          addPageLabel = <FormattedMessage id="kustom_AddPage_add__print" />;
        }
        break;
      case "mural":
        if (pagesIncrement > 1) {
          addPageLabel = <FormattedMessage id="kustom_AddPage_add_pack__mural" />;
          modalTitleLabel = (
            <FormattedMessage
              id="kustom_AddPage_add_pack_modal_title__mural"
              values={{ count: pagesIncrement }}
            />
          );
          modalLineLabel = (
            <FormattedMessage
              id="kustom_AddPage_add_pack_modal_line1__mural"
              values={{ price: formattedPrice }}
            />
          );
        } else {
          addPageLabel = <FormattedMessage id="kustom_AddPage_add__mural" />;
        }
        break;
      case "book":
        if (pagesIncrement > 1) {
          addPageLabel = <FormattedMessage id="kustom_AddPage_add_pack__book" />;
          modalTitleLabel = (
            <FormattedMessage
              id="kustom_AddPage_add_pack_modal_title__book"
              values={{ count: pagesIncrement }}
            />
          );
          modalLineLabel = (
            <FormattedMessage
              id="kustom_AddPage_add_pack_modal_line1__book"
              values={{ price: formattedPrice }}
            />
          );
        } else {
          addPageLabel = <FormattedMessage id="kustom_AddPage_add__book" />;
        }
        break;
      default: {
        const exhaustiveCheck: never = content;
        const type = exhaustiveCheck as { templateType: string };
        throw new Error(`Unsupported templateType ${type.templateType}`);
      }
    }
  }

  return (
    <div className="AddPage">
      <Button onClick={onButtonClick}>
        <IconPlusSquare height={20} width={20} />
        {addPageLabel}
      </Button>
      <ConfirmChangesModal
        display={displayModal}
        confirmLabel={intl.formatMessage({
          id: "kustom_AddPage_add_pack_modal_button_ok",
        })}
        dismissLabel={intl.formatMessage({ id: "kustom_ConfirmModal_cancel" })}
        mode="primary"
        onConfirm={() => {
          addPage();
          setModalChecked(true);
        }}
        onDismiss={() => {
          setDisplayModal(false);
          setModalChecked(true);
        }}
      >
        <h2 className="AddPage-modal-title">{modalTitleLabel}</h2>
        <p className="AddPage-modal-text">{modalLineLabel}</p>
      </ConfirmChangesModal>
    </div>
  );
};

export default AddPage;
